import {
  TmElement,
  base,
  rem,
  reset,
  body,
  spacing,
  token,
} from '@thiememeulenhoff/lit-sdk';
import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { tagName } from '../../helpers';
import { MessageState, MessageStateIcons } from './message.types';

const tag = tagName('message');

@customElement(tag)
export class MessageComponent extends TmElement {
  @property({ type: Boolean, reflect: true })
  clickable?: boolean;

  @property({ type: Boolean, reflect: true })
  deletable?: boolean;

  @property({ type: String, reflect: true })
  state?: MessageState;

  @property({ type: Boolean, reflect: true })
  type?: boolean;

  static override styles = [
    base.focus,
    reset.button,
    css`
      :where(div),
      :where(footer) {
        display: flex;
        font-size: 1em;
        gap: ${spacing(100)};
        inline-size: 100%;
        justify-content: space-between;
      }
      :where(section),
      :where(button.section) {
        align-items: flex-start;
        display: flex;
        flex-direction: column;
        text-align: start;
      }
      :where(header) {
        font-weight: 600;

        .type {
          ${body('small', 'bold')};
        }
      }
      :where(footer) {
        color: ${token('message-footer-foreground-default')};
        margin-block-start: ${spacing(50)};

        .type {
          ${body('small')};
          font-weight: 300;
        }
      }

      :where(a-icon) {
        margin-block-start: ${spacing(50)};
      }
      :where(a-icon.unread) {
        color: ${token('message-state-unread-foreground-default')};
      }
      :where(a-icon.check) {
        color: ${token('message-state-check-foreground-default')};
      }
      :where(a-icon.delete) {
        color: ${token('message-delete-foreground-default')};
      }

      :where(button.section) {
        :where(a-icon.unread):hover {
          color: ${token('message-state-unread-foreground-hover')};
        }
        :where(a-icon.check):hover {
          color: ${token('message-state-check-foreground-hover')};
        }
        :where(a-icon.delete):hover {
          color: ${token('message-delete-foreground-hover')};
        }
      }

      .status-icon {
        font-size: ${rem(8)};
      }

      .bin {
        font-size: ${rem(10)};
      }

      .main-type {
        ${body('small')};
        font-weight: 300;
      }
    `,
  ];

  #stateTemplate() {
    if (
      this.state !== undefined &&
      Object.prototype.hasOwnProperty.call(MessageStateIcons, this.state)
    ) {
      return this.#clickableSection(
        html`<a-icon
          class="${this.state} ${this.type ? 'status-icon' : ''}"
          name="${MessageStateIcons[this.state]}"
          appAnalyseClick="click-message-read"
        ></a-icon>`,
        this.#handleClickState,
        'state',
        'mark as read',
      );
    }
    return html``;
  }

  #deleteTemplate() {
    if (this.deletable) {
      return this.#clickableSection(
        html`<a-icon
          class="delete ${this.type ? 'bin' : ''}"
          name="${this.type ? 'delete-solid' : 'delete'}"
          appAnalyseClick="click-message-delete"
        ></a-icon>`,
        this.#handleClickDelete,
        'trash',
        'delete',
      );
    }
    return html``;
  }

  #footerTemplate() {
    return html`<footer>
      <span class="${this.type ? 'type' : ''}"><slot name="footer"></slot></span
      >${this.#deleteTemplate()}
    </footer>`;
  }

  #clickableSection(
    content: unknown,
    handler: unknown,
    testId?: string,
    ariaLabel?: string,
  ) {
    if (this.clickable) {
      return html`<button
        class="section"
        @click="${handler}"
        appAnalyseClick="click-message"
        data-test="${testId ?? nothing}"
        aria-label="${ariaLabel ?? nothing}"
      >
        ${content}
      </button>`;
    }
    return html`<section>${content}</section>`;
  }

  override render() {
    const content = this.#clickableSection(
      html`
        <header>
          <slot name="title" class="${this.type ? 'type' : ''}"></slot>
        </header>
        <div class="main ${this.type ? 'main-type' : ''}"><slot></slot></div>
      `,
      this.#handleClickContent,
      'content',
    );

    return html`<div ${this.type ? 'type' : ''}>
        ${content} ${this.#stateTemplate()}
      </div>
      ${this.#footerTemplate()}`;
  }

  #handleClickContent = (event: MouseEvent) => {
    this.#handleClick(event, 'clicked');
  };

  #handleClickState = (event: MouseEvent) => {
    this.#handleClick(event, 'clickedState');
  };

  #handleClickDelete = (event: MouseEvent) => {
    this.#handleClick(event, 'clickedDelete');
  };

  #handleClick = (event: MouseEvent, eventName: string) => {
    this.dispatchLocalEvent({
      eventName,
      detail: event,
    });
  };
}

declare global {
  interface HTMLElementTagNameMap {
    'a-message': MessageComponent;
  }
}
